import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { addonLineTotal, clampQuantity, type AddonQuantities } from "@/utils/addons.utils";
import { formatCurrency, cn } from "@/utils/common.utils";
import type { BillingAddon } from "@/types/billing.types";

interface AddonPickerProps {
  /** The catalogue to offer. Rows render in the order given. */
  addons: BillingAddon[];
  /** Current selection, `{ addon_id: quantity }`. Controlled. */
  value: AddonQuantities;
  onChange: (next: AddonQuantities) => void;
  /** Quantities already purchased, so a row can show what it's moving from. */
  owned?: AddonQuantities;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * Quantity steppers for billing add-ons, with a live per-line monthly cost.
 *
 * Shared by `/pricing` (where the selection rides into Checkout) and the
 * billing tab (where it posts to `/billing/addons`). The two surfaces differ in
 * what they do with the numbers, not in how the numbers are picked — so the
 * component owns the stepping and the per-line total and nothing else. Grand
 * totals and confirmation copy belong to the caller, which knows whether this
 * is a first purchase or a change to an existing subscription.
 *
 * Controlled rather than self-managing: the billing tab has to diff the draft
 * against what's already owned to know what changed, and a component holding
 * its own state would make that a `useEffect` sync.
 */
export default function AddonPicker({
  addons,
  value,
  onChange,
  owned,
  disabled = false,
  className,
  "data-testid": testId = "addon-picker",
}: AddonPickerProps) {
  const setQuantity = (addon: BillingAddon, next: number) => {
    onChange({ ...value, [addon.id]: clampQuantity(next, addon) });
  };

  return (
    <div className={cn("space-y-3", className)} data-testid={testId}>
      {addons.map(addon => {
        const quantity = value[addon.id] ?? 0;
        const ownedQuantity = owned?.[addon.id];
        const lineTotal = addonLineTotal(addon, quantity);
        const changed = ownedQuantity != null && ownedQuantity !== quantity;

        return (
          <div
            key={addon.id}
            className="flex flex-col gap-3 rounded-xl border border-dash-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`addon-row-${addon.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-dash-ink">{addon.name}</p>
              <p className="mt-0.5 text-xs leading-5 text-dash-muted">{addon.description}</p>
              <p className="mt-1 text-xs text-dash-faint">
                {formatCurrency(addon.amount)} per {addon.unit_label} / month
                {changed && (
                  <span className="ml-1 text-dash-greenDeep">
                    · currently {ownedQuantity}
                  </span>
                )}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <div className="flex items-center rounded-lg border border-dash-border">
                <Button
                  type="button"
                  variant="seamless"
                  className="px-2.5 py-2 text-dash-muted hover:text-dash-ink disabled:opacity-30"
                  onClick={() => setQuantity(addon, quantity - 1)}
                  disabled={disabled || quantity <= 0}
                  aria-label={`One fewer ${addon.unit_label}`}
                  data-testid={`addon-decrement-${addon.id}`}
                >
                  <Minus className="size-3.5" />
                </Button>
                <input
                  type="number"
                  min={0}
                  max={addon.max_quantity}
                  value={quantity}
                  onChange={e => setQuantity(addon, Number(e.target.value))}
                  disabled={disabled}
                  className="w-12 border-x border-dash-border bg-transparent py-2 text-center text-sm font-semibold text-dash-ink outline-none disabled:opacity-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  aria-label={`Number of extra ${addon.unit_label_plural}`}
                  data-testid={`addon-quantity-${addon.id}`}
                />
                <Button
                  type="button"
                  variant="seamless"
                  className="px-2.5 py-2 text-dash-muted hover:text-dash-ink disabled:opacity-30"
                  onClick={() => setQuantity(addon, quantity + 1)}
                  disabled={disabled || quantity >= addon.max_quantity}
                  aria-label={`One more ${addon.unit_label}`}
                  data-testid={`addon-increment-${addon.id}`}
                >
                  <Plus className="size-3.5" />
                </Button>
              </div>
              <span
                className="w-20 text-right text-sm font-semibold text-dash-ink"
                data-testid={`addon-line-total-${addon.id}`}
              >
                {formatCurrency(lineTotal)}
                <span className="font-normal text-dash-faint">/mo</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
